<script setup lang="ts">
import { Boxes, FolderSearch, SlidersHorizontal, Waypoints } from "@lucide/vue";
import { useBasicProjectList } from "@/api/composables/useBasicProjectList.composable";
import SearchSelectProjects from "@/components/Reusables/Inputs/SearchSelectProjects.vue";
import { LocalModelType, useCreateBuildingProject, useModels } from "@/features/BuildingProject";
import LoadingWrapper from "@/components/Reusables/Loading/LoadingWrapper/LoadingWrapper.vue";
import { GetBasicProjectListQuery } from "@/graphql";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import ParameterMapping from "./../../ParameterMapping";
import NonSelectedModels from "./NonSelectedModels.vue";
import SelectedModels from "./SelectedModels.vue";

const authState = useAuthStateStore();
const { hasAccessAllProjects, user } = storeToRefs(authState);
const models = useModels();
const createBuildingProject = useCreateBuildingProject();
const { fetchBasicProjects, basicProjects, basicProjectListLoading } = useBasicProjectList();
const isDirty = ref<boolean>(true);
const selectedBuildingProject = ref<GetBasicProjectListQuery["project"][0]>();

const emit = defineEmits<{
  submit: [speckleId?: string];
}>();

const handleOnFocus = () => {
  isDirty.value = false;
};

const handleBuildingProjectChange = (value: string) => {
  const foundProject = basicProjects.value?.find((proj) => proj.name === value);
  selectedBuildingProject.value = foundProject;

  emit("submit", foundProject?.speckleStreamId || undefined);
};

const referenceProjects = computed(() => {
  return (
    basicProjects.value
      .filter(({ speckleStreamId }) => !!speckleStreamId)
      .map((proj) => {
        return {
          name: proj.name,
          isFavorite: !!proj.favorite,
        };
      }) || []
  );
});

watch(
  () => selectedBuildingProject.value,
  (newProject) => {
    const branches: Array<LocalModelType> =
      newProject && Array.isArray(newProject.models)
        ? newProject.models.map(({ id, name }) => {
            return {
              id,
              name,
              isActive: false,
            };
          })
        : [];

    models.setModels(branches);
  }
);

watch(
  hasAccessAllProjects,
  (newHasAccessAllProjects) => {
    if (newHasAccessAllProjects === undefined) return;

    fetchBasicProjects({
      hasAccessAllProjects: newHasAccessAllProjects,
    });
  },
  { immediate: true }
);
const activeModels = computed(() => models.getActiveModels());
const inactiveModels = computed(() => models.getInactiveModels());

// The store owns the list; this forwards the edit to it.
const toggleModel = (model: LocalModelType) => models.toggleModel(model);
</script>

<template>
  <!-- The configuration view for choice 2, on the same card grammar as every other step. The first
       card's header names the choice made one click ago, since the step heading above still asks the
       question. The old InfoIcon tooltips became each card's caption line: the same words, readable
       without hovering anything. -->
  <div class="flex w-full flex-col gap-3">
    <!-- What sharing a connection means -->
    <div class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
      <div
        class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
        <div class="flex items-center gap-2">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <Waypoints
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <h2 class="t-sh4 text-type-default">A shared model connection</h2>
        </div>
      </div>
      <p class="t-small text-type-default max-w-prose">
        Connecting this project to an existing Speckle connection sends the same model data to several projects at once, which
        lets you run variance studies on a project level.
      </p>
    </div>

    <!-- Project reference -->
    <div class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
      <div
        class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
        <div class="flex items-center gap-2">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <FolderSearch
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <h2 class="t-sh4 text-type-default">Project reference</h2>
        </div>
        <p class="t-caption text-type-muted max-w-prose">Choose the project whose model connection this project should share.</p>
      </div>
      <LoadingWrapper :loading="basicProjectListLoading || !user">
        <div class="flex flex-col gap-4">
          <SearchSelectProjects
            :key="selectedBuildingProject?.id"
            :error-msg="!isDirty && !selectedBuildingProject?.id ? 'Please select a building project' : undefined"
            :keep-after-select="true"
            label="Select building project reference"
            :options="referenceProjects"
            placeholder="Available projects"
            :required="true"
            :value="selectedBuildingProject?.name"
            @focus-in="handleOnFocus"
            @option-selected="handleBuildingProjectChange" />
        </div>
      </LoadingWrapper>
    </div>

    <!-- Model configuration -->
    <div
      v-if="selectedBuildingProject"
      class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
      <div
        class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
        <div class="flex items-center gap-2">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <Boxes
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <h2 class="t-sh4 text-type-default">Model configuration</h2>
        </div>
        <p class="t-caption text-type-muted max-w-prose">Turn on the models to include from the referenced connection.</p>
      </div>
      <SelectedModels
        :models="activeModels"
        @toggle-model="toggleModel" />
      <NonSelectedModels
        :models="inactiveModels"
        @toggle-model="toggleModel" />
    </div>

    <!-- Parameter mapping -->
    <div
      v-if="selectedBuildingProject"
      class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
      <div
        class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
        <div class="flex items-center gap-2">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <SlidersHorizontal
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <h2 class="t-sh4 text-type-default">Parameter mapping</h2>
        </div>
        <p class="t-caption text-type-muted max-w-prose">
          Choose the parameter where your authoring tool stores the classification code. It is read every time a new version
          arrives through Speckle, and the code is what structures the results.
        </p>
      </div>
      <ParameterMapping :project="createBuildingProject.getProject()" />
    </div>
  </div>
</template>
